import { Order } from 'models/order';
import { formatDate, formatPatientId, formatPatientName, NO_RESPONSIBLE_USER_PLACEHOLDER } from 'utils/format';
import { TableCell, TableRow, Typography } from '@mui/material';
import React, { FC, Fragment, useMemo } from 'react';
import OrderStatusChip from 'components/orders/shared/order-status-chip.component';
import OrderItemIconArea from 'components/orders/shared/order-item-icon-area/order-item-icon-area.component';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type DraftOrderListItemProps = {
    order: Order;
    onDeleteClick: (orderId: string) => void;
};
const DraftOrderListItem: FC<DraftOrderListItemProps> = ({ order, onDeleteClick }: DraftOrderListItemProps) => {
    const navigate = useNavigate();
    const { t: translate } = useTranslation(['orders']);

    const navigateToOrderCreatePage = () => {
        navigate(`/orders/create-action/draft/${order._id}`);
    };

    const handleDeleteClick = () => {
        onDeleteClick(order._id);
    };

    const actionCodes = useMemo(
        () =>
            (order?.restorations || []).flatMap((restoration) =>
                (restoration.actions || []).map((action) => action.code || ''),
            ),
        [order],
    );

    return (
        <Fragment>
            <TableRow sx={{ cursor: 'pointer' }} hover onClick={navigateToOrderCreatePage}>
                <TableCell>
                    <OrderStatusChip status={order.status} />
                </TableCell>
                <TableCell>
                    <div>{order.orderId}</div>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }} className="mouseflow-mask">
                    <div>{formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}</div>
                    <div>{formatPatientId(order.patient)}</div>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>{actionCodes?.length ? actionCodes.join(', ') : '-'}</TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                    {order.dentist?.fullName ?? NO_RESPONSIBLE_USER_PLACEHOLDER}
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>{order.supplier?.name ? order.supplier.name : '-'}</TableCell>
                <TableCell sx={{ maxWidth: 180 }}>
                    {order.draftCreatedAt ? <div>{formatDate(new Date(order.draftCreatedAt), 'datetime')}</div> : '-'}
                    {order.draftCreatedBy?.fullName !== order.dentist?.fullName ? (
                        <div>
                            <Typography variant="caption">{order.draftCreatedBy?.fullName}</Typography>
                        </div>
                    ) : undefined}
                </TableCell>
                <TableCell sx={{ minWidth: 180 }}>
                    {order.draftUpdatedAt ? <div>{formatDate(new Date(order.draftUpdatedAt), 'datetime')}</div> : '-'}
                </TableCell>
                <TableCell align="right">
                    <OrderItemIconArea order={order} deleteDraftOrder={handleDeleteClick} />
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

export default DraftOrderListItem;
